'use client';

import { ReactNode, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
// anime.js'yi lazy loading ile kullanmak
// import anime from 'animejs';

interface AnimatedSectionProps {
  children: ReactNode;
  delay?: number;
  className?: string;
  animation?: 'fadeIn' | 'slideUp' | 'slideLeft' | 'slideRight' | 'zoom' | 'none';
  duration?: number;
  easing?: string;
  threshold?: number;
  once?: boolean;
}

const AnimatedSection = ({
  children,
  delay = 0,
  className = '',
  animation = 'fadeIn',
  duration = 800,
  easing = 'easeOutQuad',
  threshold = 0.2,
  once = true,
}: AnimatedSectionProps) => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const hasAnimated = useRef<boolean>(false);

  // Mobilde ağır animasyonları devre dışı bırak
  let effectiveAnimation = animation;
  if (typeof window !== 'undefined' && window.innerWidth <= 768) {
    effectiveAnimation = 'none';
  }

  useEffect(() => {
    if (!sectionRef.current) return;

    const section = sectionRef.current;
    
    const handleIntersection = async (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      
      if (entry.isIntersecting && (!once || !hasAnimated.current)) {
        hasAnimated.current = true;
        
        // Animasyon aktif değilse hemen return
        if (effectiveAnimation === 'none') {
          section.style.opacity = '1';
          return;
        }
        
        // Sadece görünür olduğunda anime.js'yi import et
        const animeModule = await import('animejs');
        const anime = animeModule.default;
        
        let animationConfig: any = {
          targets: section,
          opacity: [0, 1],
          duration: duration,
          easing: easing,
          delay: delay
        };
        
        switch (effectiveAnimation) {
          case 'fadeIn':
            animationConfig = {
              ...animationConfig,
              opacity: [0, 1]
            };
            break;
          case 'slideUp':
            animationConfig = {
              ...animationConfig,
              translateY: [50, 0],
              opacity: [0, 1]
            };
            break;
          case 'slideLeft':
            animationConfig = {
              ...animationConfig,
              translateX: [-100, 0],
              opacity: [0, 1]
            };
            break;
          case 'slideRight':
            animationConfig = {
              ...animationConfig,
              translateX: [100, 0],
              opacity: [0, 1]
            };
            break;
          case 'zoom':
            animationConfig = {
              ...animationConfig,
              scale: [0.9, 1],
              opacity: [0, 1]
            };
            break;
        }
        
        anime(animationConfig);
      }
    };
    
    const observer = new IntersectionObserver(handleIntersection, {
      threshold: threshold,
      rootMargin: '20px' // Biraz daha önce yüklemeye başla
    });
    
    observer.observe(section);
    
    // İlk render için görünür durumdaysa
    if (section.getBoundingClientRect().top < window.innerHeight * threshold) {
      handleIntersection([{ isIntersecting: true } as IntersectionObserverEntry]);
    }
    
    return () => {
      observer.disconnect();
    };
  }, [effectiveAnimation, delay, duration, easing, once, threshold]);

  return (
    <div
      ref={sectionRef}
      className={cn('opacity-0', className)}
      style={{ willChange: 'opacity, transform' }}
    >
      {children}
    </div>
  );
};

export default AnimatedSection; 